import { FormEvent, Suspense, useEffect, useState } from 'react';

import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { OPEN_ERROR_MODAL } from '@iwModals/Modals.actions';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    LapiError,
    fetchGetMcaEstimatedSchedule,
    fetchPutFormalOffersMerchantCashAdvanceConfiguration
} from '@iwoca/lapi-client/edge';
import { Button, Spinner } from '@iwoca/orion';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import LoadingCalculationValue from './components/LoadingCalculationValue';
import TipBox from './components/TipBox';
import messages from './CustomiseOffer.messages';
import styles from './CustomiseOffer.module.css';
import EstimatedRepayments from './EstimatedRepayments';
import { WithdrawalSummary } from './withdrawalSummary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { ChooseLoanAmount } from '../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';
import { ChooseSweepAmount } from '../../../sharedDashboardComponents/slider/chooseSweepAmount/ChooseSweepAmount';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: ({
        amount,
        sweep,
        offerId
    }: {
        amount: number;
        sweep: number;
        offerId: string;
    }) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: amount,
                sweep: sweep
            })
        });
    },
    openErrorModal: () =>
        dispatch({
            type: OPEN_ERROR_MODAL,
            id: 'FallbackErrorModal'
        })
});

type TCustomiseOffer = { accountId: string } & ReturnType<
    typeof mapDispatchToProps
>;

const CustomiseOffer = ({
    accountId,
    startDrawdown,
    openErrorModal
}: TCustomiseOffer) => {
    const stateKey = useStateKey();

    const [drawdownAmount, setDrawdownAmount] = useState(0);
    const [sweep, setSweep] = useState(0.0);

    const [sweepVals, setSweepVals] = useState({
        absMin: 0,
        absMax: 0,
        min: 0,
        max: 0
    });
    const [amountVals, setAmountVals] = useState({
        min: 0,
        max: 0
    });

    const convertSweepToPercentage = (sweep: number) => {
        return Math.round(sweep * 10000) / 100;
    };
    const convertSweepToDecimal = (sweep: number) => {
        return Math.round(sweep * 100) / 10000;
    };

    // Data Fetching
    const { data: accounts, isError: accountsError } = useQuery(
        getAccountsQueryConfig(stateKey)
    );
    const accountWithLatestFormalOffer = accounts?.data.accounts.find(
        (account) => account.latest_formal_offer
    );
    const offer_id = accountWithLatestFormalOffer?.latest_formal_offer.offer_id;

    const {
        data: schedule,
        isFetching: scheduleLoading,
        error: scheduleError,
        isInitialLoading
    } = useQuery({
        queryKey: ['mcaSchedule', stateKey, offer_id, drawdownAmount, sweep],
        queryFn: () => {
            return fetchGetMcaEstimatedSchedule({
                stateKey: stateKey,
                offerId: offer_id!,
                query: {
                    cash_amount:
                        drawdownAmount > 0 ? drawdownAmount : undefined,
                    sweep_fraction:
                        sweep > 0 ? convertSweepToDecimal(sweep) : undefined
                }
            });
        },
        enabled: Boolean(offer_id),
        keepPreviousData: true
    });

    // Set intial amount values
    if (drawdownAmount === 0 && schedule) {
        const initialAmount = Math.floor(
            schedule.data.summary.amount.maximum_transaction_amount
        );
        setDrawdownAmount(initialAmount);
        setAmountVals({
            min: Math.ceil(
                schedule.data.summary.amount.minimum_transaction_amount
            ),
            max: initialAmount
        });
    }

    // Set intial sweep values
    if (sweep === 0 && schedule) {
        const initialSweepMin = convertSweepToPercentage(
            schedule.data.summary.sweep.minimum_sweep_fraction
        );
        const initialSweepMax = convertSweepToPercentage(
            schedule.data.summary.sweep.maximum_sweep_fraction
        );
        setSweep(initialSweepMin);
        setSweepVals({
            absMin: 0.1,
            absMax: initialSweepMax,
            min: initialSweepMin,
            max: initialSweepMax
        });
    }

    useEffect(() => {
        const checkSweepInRange = (sweep: number, min: number, max: number) => {
            if (sweep === 0 || !(sweep >= min && sweep <= max)) {
                setSweep(max);
            }
        };

        if (schedule) {
            const newSweepMin = convertSweepToPercentage(
                schedule.data.summary.sweep.minimum_sweep_fraction
            );
            const newSweepMax = convertSweepToPercentage(
                schedule.data.summary.sweep.maximum_sweep_fraction
            );
            setSweepVals((prevSweepVals) => ({
                ...prevSweepVals,
                min: newSweepMin,
                max: newSweepMax
            }));

            checkSweepInRange(sweep, newSweepMin, newSweepMax);

            setAmountVals({
                min: Math.ceil(
                    schedule.data.summary.amount.minimum_transaction_amount
                ),
                max: Math.floor(
                    schedule.data.summary.amount.maximum_transaction_amount
                )
            });
        }
    }, [schedule, setSweepVals, sweep, setSweep]);

    // API Errors
    if (
        accountsError ||
        (scheduleError && (scheduleError as LapiError)?.statusCode !== 400)
    ) {
        openErrorModal();
    }

    if (isInitialLoading) {
        return (
            <div
                className={styles.spinner}
                data-testid="rblOfferLoadingSpinner"
            >
                <Spinner size="large" />
            </div>
        );
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (offer_id) {
            try {
                tracking.ampli.drawdownCtaClicked();

                await fetchPutFormalOffersMerchantCashAdvanceConfiguration({
                    stateKey,
                    offerId: offer_id,
                    body: {
                        data: {
                            cash_amount: drawdownAmount,
                            revenue_sweep_fraction: convertSweepToDecimal(sweep)
                        }
                    }
                });
                startDrawdown({
                    amount: drawdownAmount,
                    sweep: convertSweepToDecimal(sweep),
                    offerId: offer_id
                });
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (err) {
                openErrorModal();
            }
        }
    };

    return (
        <form className={styles.approvedForm} onSubmit={(e) => handleSubmit(e)}>
            <ChooseLoanAmount
                drawdownAmount={drawdownAmount}
                minimumDrawdownAmount={amountVals.min}
                maximumDrawdownAmount={amountVals.max}
                updateDrawdownAmount={setDrawdownAmount}
            />
            <ChooseSweepAmount
                label={messages.drawdownSweepHeader}
                sweep={sweep}
                absSweepMax={sweepVals.absMax}
                absSweepMin={sweepVals.absMin}
                sweepMax={sweepVals.max}
                sweepMin={sweepVals.min}
                updateSweepValue={(value) => setSweep(value)}
            />

            {schedule && (
                <EstimatedRepayments
                    className={styles.loanSummary}
                    schedule={schedule}
                    loading={scheduleLoading}
                />
            )}
            {schedule && (
                <Suspense fallback={<MainContentPending />}>
                    <WithdrawalSummary
                        accountId={accountId}
                        schedule={schedule}
                        loading={scheduleLoading}
                        drawdownAmount={drawdownAmount}
                    />
                </Suspense>
            )}

            {schedule && (
                <TipBox
                    tip={
                        <FormattedMessage
                            {...messages.minimumRepaymentsNotice}
                            values={{
                                minimumAmount: (
                                    <LoadingCalculationValue>
                                        <MoneyNoDecimals
                                            className={
                                                styles.minimumPaymentsHighlights
                                            }
                                            value={
                                                schedule.data.summary
                                                    .minimum_repayments.amount
                                            }
                                        />
                                    </LoadingCalculationValue>
                                ),
                                minimumRepaymentInterval: (
                                    <span
                                        className={
                                            styles.minimumPaymentsHighlights
                                        }
                                    >
                                        <FormattedMessage
                                            {...messages.repaymentInterval}
                                            values={{
                                                interval:
                                                    schedule.data.summary
                                                        .minimum_repayments
                                                        .interval.amount
                                            }}
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                />
            )}

            <Button
                className={styles.cta}
                type="submit"
                variant="success"
                disabled={
                    scheduleLoading || drawdownAmount === 0 || sweep === 0
                }
                data-testid="start-drawdown"
            >
                <FormattedMessage
                    {...messages.cta}
                    values={{
                        amount: (
                            <MoneyNoDecimals
                                className={styles.summaryAmount}
                                value={drawdownAmount}
                            />
                        )
                    }}
                />
            </Button>
        </form>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(CustomiseOffer);
